import { getAuthToken } from '../../services/auth/tokenStorage';
import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  SafeAreaView,
  ScrollView,
  TextInput,
  Dimensions,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../config';

import { createLogger } from '../../utils/logger';
import { analytics, AnalyticsEvent } from '../../services/analytics';
const logger = createLogger('UserOnboarding');

const { width } = Dimensions.get('window');

type OnboardingData = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  username: string;
  planningStage: string;
  planningFor: string;
  planningStatus: string;
  storageOption: string;
  promoCode: string;
  // Successor/Keyholder validation fields
  isInvitedUser: boolean;
  validationQuestion: string;
  validationAnswer: string;
  phoneNumber: string;
  primaryUserName: string;
};

const UserOnboardingScreen: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<any>>();
  const [step, setStep] = useState(0); // Start at 0 for invitation check
  const [data, setData] = useState<OnboardingData>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    username: '', // Will be set to email automatically
    planningStage: '',
    planningFor: '',
    planningStatus: '',
    storageOption: '',
    promoCode: '',
    // Successor/Keyholder validation fields
    isInvitedUser: false,
    validationQuestion: '',
    validationAnswer: '',
    phoneNumber: '',
    primaryUserName: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [promoCodeValid, setPromoCodeValid] = useState<boolean | null>(null);
  const [promoCodeError, setPromoCodeError] = useState<string>('');
  // Validation states
  const [validationAttempts, setValidationAttempts] = useState(0);
  const [validationError, setValidationError] = useState<string>('');
  const [isLoadingValidation, setIsLoadingValidation] = useState(false);
  const [validationSuccess, setValidationSuccess] = useState(false);

  const planningStageOptions = [
    {
      title: 'End of Life Planning',
      description: 'Want to make sure everything is in order for my loved ones.',
    },
    {
      title: 'Emergency Planning',
      description: 'Just in case something happens',
    },
    {
      title: 'Early Planning',
      description: 'Just getting ahead of the game',
    },
  ];

  const planningForOptions = [
    {
      title: 'Single',
      description: 'Focus on next of kin',
    },
    {
      title: 'Early Planning',
      description: 'Just getting ahead of the game',
    },
    {
      title: 'In a relationship',
      description: 'Focus on relationship & next of kin',
    },
    {
      title: 'Married',
      description: 'Focus on spouse',
    },
    {
      title: 'Married with children',
      description: 'Focus on spouse & children succession',
    },
  ];

  const planningStatusOptions = [
    {
      title: 'Beginning',
      description: 'Just starting and don\'t know where to start the process.',
    },
    {
      title: 'Started',
      description: 'Have done some planning, but it is scattered and not organized.',
    },
    {
      title: 'Completed',
      description: 'I have a Will or Trust completed and have most things in order.',
    },
  ];

  const storageOptions = [
    {
      title: 'Free - Checklist only',
      description: 'No data is stored. This is only a checklist of tasks to complete.',
      price: 'Free',
    },
    {
      title: 'Premium',
      description: 'Sensitive data is encrypted, and ALL data is stored and backed-up in the cloud.',
      price: '$10/yr',
    },
    {
      title: 'Premium and Document Storage',
      description: 'All premium data storage and added document upload and storage',
      price: '$99/yr',
    },
  ];

  const renderProgressBar = () => {
    // Don't show progress bar for step 0 (invitation check)
    if (step === 0) return null;

    return (
      <View style={styles.progressContainer}>
        {[1, 2, 3, 4].map((index) => (
          <View
            key={index}
            style={[
              styles.progressBar,
              {
                backgroundColor: index <= step ? '#29BFC0' : '#E5E7EB',
              },
            ]}
          />
        ))}
      </View>
    );
  };

  const renderOptionCard = (
    title: string,
    description: string,
    isSelected: boolean,
    onSelect: () => void,
    price?: string,
    key?: string
  ) => (
    <TouchableOpacity
      key={key}
      style={[styles.optionCard, isSelected && styles.selectedCard]}
      onPress={onSelect}
    >
      <View style={styles.radioButton}>
        {isSelected && <View style={styles.radioButtonInner} />}
      </View>
      <View style={styles.optionContent}>
        <Text style={styles.optionTitle}>{title}</Text>
        <Text style={styles.optionDescription}>{description}</Text>
        {price && <Text style={styles.optionPrice}>{price}</Text>}
      </View>
    </TouchableOpacity>
  );

  // Load email from AsyncStorage and check for invitation on mount
  React.useEffect(() => {
    const loadEmailAndCheckInvitation = async () => {
      const email = await AsyncStorage.getItem('@signup_email');
      if (email) {
        setData(prev => ({ ...prev, email, username: email }));

        // Check if this email has a pending invitation
        await checkForInvitation(email);
      }
    };
    loadEmailAndCheckInvitation();
  }, []);

  // Check if user has a pending invitation
  const checkForInvitation = async (email: string) => {
    try {
      const response = await fetch(`${API_URL}/auth/get-validation-question`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (result.hasInvitation) {
        setData(prev => ({
          ...prev,
          validationQuestion: result.validationQuestion || '',
          primaryUserName: result.primaryUserName || '',
        }));
      }
    } catch (error) {
      logger.error('Error checking for invitation:', error);
    }
  };

  // Format phone number as (xxx) xxx-xxxx
  const formatPhoneNumber = (text: string) => {
    const cleaned = text.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (!match) return text;

    const [, area, prefix, line] = match;
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${area}) ${prefix}`;
    return `(${area}) ${prefix}-${line}`;
  };

  // Handle validation answer submission
  const handleValidationSubmit = async () => {
    if (!data.validationAnswer.trim() || !data.phoneNumber.trim()) {
      setValidationError('Please provide both your answer and phone number');
      return;
    }

    if (validationAttempts >= 3) {
      setValidationError('Maximum validation attempts exceeded. You can continue signup, but data access will be restricted until validated.');
      return;
    }

    setIsLoadingValidation(true);
    setValidationError('');

    try {
      const response = await fetch(`${API_URL}/auth/verify-validation-answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email,
          answer: data.validationAnswer,
          phoneNumber: data.phoneNumber,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setValidationSuccess(true);
        setValidationError('');
        // Proceed to step 1 after a short delay
        setTimeout(() => {
          setStep(1);
        }, 1000);
      } else {
        setValidationAttempts(prev => prev + 1);
        const remainingAttempts = 3 - (validationAttempts + 1);

        let errorMsg = 'Validation failed. ';
        if (!result.phoneMatches && !result.answerMatches) {
          errorMsg += 'Both phone number and answer are incorrect.';
        } else if (!result.phoneMatches) {
          errorMsg += 'Phone number does not match.';
        } else if (!result.answerMatches) {
          errorMsg += 'Answer is incorrect.';
        }

        if (remainingAttempts > 0) {
          errorMsg += ` ${remainingAttempts} attempt${remainingAttempts > 1 ? 's' : ''} remaining.`;
        }

        setValidationError(errorMsg);
      }
    } catch (error) {
      logger.error('Error validating answer:', error);
      setValidationError('An error occurred during validation. Please try again.');
    } finally {
      setIsLoadingValidation(false);
    }
  };

  // Request new validation from primary user
  const handleRequestNewValidation = async () => {
    setIsLoadingValidation(true);
    try {
      const response = await fetch(`${API_URL}/auth/request-new-validation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email, // This will be looked up to find primary user
          successorEmail: data.email,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setValidationError('');
        alert('Validation request sent! The primary user will be notified to update your validation question.');
      } else {
        setValidationError('Failed to send validation request. Please try again.');
      }
    } catch (error) {
      logger.error('Error requesting new validation:', error);
      setValidationError('An error occurred. Please try again.');
    } finally {
      setIsLoadingValidation(false);
    }
  };

  const validatePromoCode = async () => {
    if (!data.promoCode.trim()) {
      setPromoCodeValid(null);
      setPromoCodeError('');
      return;
    }

    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_URL}/payment/validate-promo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ code: data.promoCode.trim() }),
      });

      if (response.ok) {
        const result = await response.json();
        setPromoCodeValid(true);
        setPromoCodeError('');
        // You can store the coupon details if needed
      } else {
        setPromoCodeValid(false);
        setPromoCodeError('Invalid promo code');
      }
    } catch (error) {
      setPromoCodeValid(false);
      setPromoCodeError('Failed to validate promo code');
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <View style={styles.stepContainer}>
            <Text style={styles.stepTitle}>Welcome to FinalKey!</Text>
            <Text style={styles.stepDescription}>
              Were you invited to be a successor or keyholder?
            </Text>

            <View style={styles.invitationButtonContainer}>
              <TouchableOpacity
                style={[styles.invitationButton, styles.yesButton]}
                onPress={() => setData({ ...data, isInvitedUser: true })}
              >
                <Text style={[styles.invitationButtonText, styles.yesText]}>Yes</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.invitationButton, styles.noButton]}
                onPress={() => {
                  setData({ ...data, isInvitedUser: false });
                  setStep(1); // Skip validation, go to step 1
                }}
              >
                <Text style={[styles.invitationButtonText, styles.noText]}>No</Text>
              </TouchableOpacity>
            </View>

            {data.isInvitedUser && data.validationQuestion && (
              <View style={styles.validationContainer}>
                <Text style={styles.validationTitle}>
                  Validation from {data.primaryUserName}
                </Text>
                <Text style={styles.validationQuestion}>{data.validationQuestion}</Text>

                <TextInput
                  style={styles.input}
                  placeholder="Your answer"
                  value={data.validationAnswer}
                  onChangeText={(text) => setData({ ...data, validationAnswer: text })}
                  placeholderTextColor="#A9A9A9"
                  editable={validationAttempts < 3}
                />

                <TextInput
                  style={styles.input}
                  placeholder="Phone number"
                  value={data.phoneNumber}
                  onChangeText={(text) => {
                    const formatted = formatPhoneNumber(text);
                    setData({ ...data, phoneNumber: formatted });
                  }}
                  placeholderTextColor="#A9A9A9"
                  keyboardType="phone-pad"
                  maxLength={14}
                  editable={validationAttempts < 3}
                />

                {validationSuccess && (
                  <View style={styles.successMessage}>
                    <Text style={styles.successText}>✓ Validation successful!</Text>
                  </View>
                )}

                {validationError && (
                  <View style={styles.errorMessage}>
                    <Text style={styles.errorText}>{validationError}</Text>
                  </View>
                )}

                {validationAttempts < 3 && !validationSuccess && (
                  <TouchableOpacity
                    style={[styles.validateButton, isLoadingValidation && styles.validateButtonDisabled]}
                    onPress={handleValidationSubmit}
                    disabled={isLoadingValidation}
                  >
                    {isLoadingValidation ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.validateButtonText}>Verify</Text>
                    )}
                  </TouchableOpacity>
                )}

                {validationAttempts >= 3 && (
                  <View style={styles.maxAttemptsContainer}>
                    <Text style={styles.maxAttemptsText}>
                      Maximum validation attempts reached. You can continue with signup, but data access will be restricted until validated.
                    </Text>
                    <TouchableOpacity
                      style={styles.requestNewButton}
                      onPress={handleRequestNewValidation}
                      disabled={isLoadingValidation}
                    >
                      {isLoadingValidation ? (
                        <ActivityIndicator color="#29BFC0" />
                      ) : (
                        <Text style={styles.requestNewButtonText}>Request New Validation</Text>
                      )}
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.skipButton}
                      onPress={() => setStep(1)}
                    >
                      <Text style={styles.skipButtonText}>Continue Anyway</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            )}

            {data.isInvitedUser && !data.validationQuestion && (
              <View style={styles.noInvitationContainer}>
                <Text style={styles.noInvitationText}>
                  No pending invitation found for this email. Please contact the person who invited you.
                </Text>
                <TouchableOpacity
                  style={styles.skipButton}
                  onPress={() => {
                    setData({ ...data, isInvitedUser: false });
                    setStep(1);
                  }}
                >
                  <Text style={styles.skipButtonText}>Continue as Primary User</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        );

      case 1:
         return (
           <View style={styles.stepContainer}>
             <Text style={styles.stepTitle}>Tell Us About Yourself</Text>
             <Text style={styles.stepDescription}>
               Tell us a bit about yourself so we can tailor your experience and help you
               setup your financial accounts, health care directives, estate plans, and
               personal wishes. Your personalized journey starts here!
             </Text>
             <TextInput
               style={styles.input}
               placeholder="First Name"
               value={data.firstName}
               onChangeText={(text) => setData({ ...data, firstName: text })}
               placeholderTextColor="#A9A9A9"
             />
             <TextInput
               style={styles.input}
               placeholder="Last Name"
               value={data.lastName}
               onChangeText={(text) => setData({ ...data, lastName: text })}
               placeholderTextColor="#A9A9A9"
             />
           </View>
         );

      case 2:
        return (
          <View style={styles.stepContainer}>
            <Text style={styles.stepTitle}>What are you planning for?</Text>
            <Text style={styles.stepDescription}>What stage in life are you?</Text>
            <ScrollView style={styles.optionsScrollView}>
              {planningStageOptions.map((option, index) =>
                renderOptionCard(
                  option.title,
                  option.description,
                  data.planningStage === option.title,
                  () => setData({ ...data, planningStage: option.title }),
                  undefined,
                  `planning-stage-${index}`
                )
              )}
            </ScrollView>
          </View>
        );

      case 3:
        return (
          <View style={styles.stepContainer}>
            <Text style={styles.stepTitle}>Who are you planning for?</Text>
            <Text style={styles.stepDescription}>Who is your focus for planning purposes?</Text>
            <ScrollView style={styles.optionsScrollView}>
              {planningForOptions.map((option, index) =>
                renderOptionCard(
                  option.title,
                  option.description,
                  data.planningFor === option.title,
                  () => setData({ ...data, planningFor: option.title }),
                  undefined,
                  `planning-for-${index}`
                )
              )}
            </ScrollView>
          </View>
        );

      case 4:
        const isPremiumSelected = data.storageOption === 'Premium' || data.storageOption === 'Premium and Document Storage';
        return (
          <View style={styles.stepContainer}>
            <Text style={styles.stepTitle}>How do you plan to use the App?</Text>
            <Text style={styles.stepDescription}>Select how you plan store your data</Text>
            <ScrollView style={styles.optionsScrollView}>
              {storageOptions.map((option, index) =>
                renderOptionCard(
                  option.title,
                  option.description,
                  data.storageOption === option.title,
                  () => setData({ ...data, storageOption: option.title }),
                  option.price,
                  `storage-option-${index}`
                )
              )}
              {isPremiumSelected && (
                <View style={styles.promoCodeContainer}>
                  <Text style={styles.promoCodeLabel}>Have a promo code? (Optional)</Text>
                  <View style={styles.promoCodeInputContainer}>
                    <TextInput
                      style={[
                        styles.promoCodeInput,
                        promoCodeValid === true && styles.promoCodeInputValid,
                        promoCodeValid === false && styles.promoCodeInputInvalid,
                      ]}
                      placeholder="Enter promo code"
                      value={data.promoCode}
                      onChangeText={(text) => {
                        setData({ ...data, promoCode: text });
                        setPromoCodeValid(null);
                        setPromoCodeError('');
                      }}
                      placeholderTextColor="#A9A9A9"
                      autoCapitalize="characters"
                    />
                    <TouchableOpacity
                      style={styles.validateButton}
                      onPress={validatePromoCode}
                    >
                      <Text style={styles.validateButtonText}>Validate</Text>
                    </TouchableOpacity>
                  </View>
                  {promoCodeValid === true && (
                    <Text style={styles.promoCodeSuccess}>✓ Valid promo code</Text>
                  )}
                  {promoCodeError && (
                    <Text style={styles.promoCodeError}>{promoCodeError}</Text>
                  )}
                </View>
              )}
            </ScrollView>
          </View>
        );

      default:
        return null;
    }
  };

  const handleContinue = async () => {
    // Step 0: Validation check - handled by button clicks within step
    if (step === 0) {
      // Don't allow continue if they selected "Yes" but haven't validated or skipped
      if (data.isInvitedUser && data.validationQuestion && !validationSuccess && validationAttempts < 3) {
        setValidationError('Please complete validation or continue anyway after 3 attempts.');
        return;
      }
      setStep(1);
      return;
    }

    if (step === 1 && (!data.firstName || !data.lastName)) {
      // Show error or alert
      return;
    }

    if (step < 4) {
      setStep(step + 1);
    } else {
      // Handle completion - save to backend
      await handleOnboardingCompletion();
    }
  };

  const handleOnboardingCompletion = async () => {
    setIsSubmitting(true);
    
    try {
      const token = await getAuthToken();
      if (!token) {
        logger.error('No token found');
        return;
      }

      const response = await fetch(`${API_URL}/auth/complete-onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          firstName: data.firstName,
          lastName: data.lastName,
          username: data.username,
          planningStage: data.planningStage,
          planningFor: data.planningFor,
          planningStatus: data.planningStatus,
          storageOption: data.storageOption,
          promoCode: data.promoCode.trim() || undefined,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        logger.debug('Onboarding completed successfully');
        analytics.track(AnalyticsEvent.OnboardingCompleted, {
          planning_stage: data.planningStage,
          planning_for: data.planningFor,
          storage_option: data.storageOption,
        });

        // Check if there's a pending invitation to accept
        const invitationToken = await AsyncStorage.getItem('@invitation_token');

        if (invitationToken) {
          // Navigate to AcceptInvitation screen with the stored token
          navigation.navigate('AcceptInvitation', { token: invitationToken });
          return;
        }

        // Navigate based on storage option
        if (
          data.storageOption === 'Premium' ||
          data.storageOption === 'Premium and Document Storage'
        ) {
          // Premium options: plans are bought through the store on the
          // Billing screen (the old in-app card form is gone, 2026-09-24).
          navigation.navigate('Profile', { screen: 'SubscriptionManagement' });
        } else {
          // Free option - go directly to Profile (Settings)
          navigation.reset({
            index: 0,
            routes: [{ name: 'Profile' }],
          });
        }
      } else {
        logger.error('Failed to complete onboarding:', result.message);
        // Handle error - show alert
      }
    } catch (error) {
      logger.error('Error completing onboarding:', error);
      // Handle error - show alert
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <TouchableOpacity
        style={styles.backButton}
        onPress={() => {
          if (step > 0) {
            setStep(step - 1);
          } else {
            navigation.goBack();
          }
        }}
      >
        <Image
          source={require('../../assets/icons/back.png')}
          style={styles.backIcon}
        />
      </TouchableOpacity>

      <Image
        source={require('../../assets/logo.png')}
        style={styles.logo}
        resizeMode="contain"
      />

      {step > 0 && (
        <View style={styles.stepIndicator}>
          <Text style={styles.stepText}>[{step}/4]</Text>
          {renderProgressBar()}
        </View>
      )}

      <ScrollView style={styles.content}>
        {renderStep()}
      </ScrollView>

      {/* Hide continue button on step 0 unless user has chosen "No" or validation is complete */}
      {(step > 0 ||
        (step === 0 && data.isInvitedUser === false) ||
        (step === 0 && validationSuccess) ||
        (step === 0 && validationAttempts >= 3)) && (
        <TouchableOpacity
          style={[styles.continueButton, isSubmitting && styles.continueButtonDisabled]}
          onPress={handleContinue}
          disabled={isSubmitting}
        >
          <Text style={styles.continueButtonText}>
            {isSubmitting ? 'Completing...' : step === 4 ? 'Complete' : 'Continue'}
          </Text>
        </TouchableOpacity>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  backButton: {
    position: 'absolute',
    top: ms(50),
    left: ms(20),
    zIndex: 1,
  },
  backIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#29BFC0',
  },
  logo: {
    width: s(80),
    height: s(80),
    alignSelf: 'center',
    marginTop: ms(40),
  },
  stepIndicator: {
    alignItems: 'center',
    marginTop: ms(20),
  },
  stepText: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  progressContainer: {
    flexDirection: 'row',
    width: width - 40,
    justifyContent: 'space-between',
    paddingHorizontal: ms(20),
  },
  progressBar: {
    flex: 1,
    height: s(4),
    backgroundColor: '#E5E7EB',
    marginHorizontal: ms(2),
    borderRadius: ms(2),
  },
  content: {
    flex: 1,
    marginTop: ms(20),
  },
  stepContainer: {
    padding: ms(20),
  },
  stepTitle: {
    fontSize: ms(28, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    textAlign: 'center',
    marginBottom: ms(10),
  },
  stepDescription: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(30),
    paddingHorizontal: ms(20),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(15),
    marginBottom: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    elevation: 2,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
  },
  optionsScrollView: {
    flex: 1,
    paddingTop: ms(10),
    paddingBottom: ms(40),
  },
  optionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(15),
    flexDirection: 'row',
    alignItems: 'center',
  },
  selectedCard: {
    borderColor: '#29BFC0',
    borderWidth: 2,
  },
  radioButton: {
    width: s(24),
    height: s(24),
    borderRadius: ms(12),
    borderWidth: 2,
    borderColor: '#29BFC0',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: ms(15),
  },
  radioButtonInner: {
    width: s(12),
    height: s(12),
    borderRadius: ms(6),
    backgroundColor: '#29BFC0',
  },
  optionContent: {
    flex: 1,
  },
  optionTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(5),
  },
  optionDescription: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
  },
  optionPrice: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(5),
    fontWeight: '600',
  },
  continueButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    padding: ms(15),
    margin: ms(20),
    alignItems: 'center',
    elevation: 2,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
  },
  continueButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  continueButtonDisabled: {
    backgroundColor: '#A9A9A9',
    opacity: 0.7,
  },
  promoCodeContainer: {
    marginTop: ms(20),
    padding: ms(15),
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  promoCodeLabel: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(10),
    fontWeight: '500',
  },
  promoCodeInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(10),
  },
  promoCodeInput: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderRadius: ms(10),
    padding: ms(12),
    fontSize: ms(16, 0.3),
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  promoCodeInputValid: {
    borderColor: '#10B981',
    borderWidth: 2,
  },
  promoCodeInputInvalid: {
    borderColor: '#EF4444',
    borderWidth: 2,
  },
  validateButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(10),
    paddingVertical: ms(12),
    paddingHorizontal: ms(20),
  },
  validateButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  promoCodeSuccess: {
    marginTop: ms(8),
    fontSize: ms(14, 0.3),
    color: '#10B981',
    fontWeight: '500',
  },
  promoCodeError: {
    marginTop: ms(8),
    fontSize: ms(14, 0.3),
    color: '#EF4444',
    fontWeight: '500',
  },
  usernameInputContainer: {
    position: 'relative',
    width: '100%',
  },
  usernameIndicator: {
    position: 'absolute',
    right: ms(16),
    top: '50%',
    transform: [{ translateY: -12 }],
  },
  usernameAvailableIcon: {
    position: 'absolute',
    right: ms(16),
    top: '50%',
    transform: [{ translateY: -12 }],
    width: s(24),
    height: s(24),
    tintColor: '#4CAF50',
  },
  validInput: {
    borderColor: '#4CAF50',
  },
  errorInput: {
    borderColor: '#E85C5C',
  },
  errorText: {
    color: '#E85C5C',
    fontSize: ms(14, 0.3),
    marginTop: ms(8),
  },
  helperText: {
    color: '#6B7280',
    fontSize: ms(14, 0.3),
    marginTop: ms(8),
  },
  // Step 0: Invitation validation styles
  invitationButtonContainer: {
    width: '100%',
    paddingHorizontal: 0,
    marginTop: ms(20),
    marginBottom: ms(20),
  },
  invitationButton: {
    width: '100%',
    borderRadius: ms(30),
    padding: ms(15),
    alignItems: 'center',
    marginBottom: ms(15),
  },
  yesButton: {
    backgroundColor: '#29BFC0',
  },
  noButton: {
    backgroundColor: '#FF9F64',
  },
  invitationButtonText: {
    fontSize: ms(16, 0.3),
    fontWeight: '500',
  },
  yesText: {
    color: '#FFFFFF',
  },
  noText: {
    color: '#FFFFFF',
  },
  validationContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginTop: ms(20),
  },
  validationTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
    textAlign: 'center',
  },
  validationQuestion: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(20),
    textAlign: 'center',
    fontStyle: 'italic',
  },
  successMessage: {
    backgroundColor: '#D1FAE5',
    borderRadius: ms(10),
    padding: ms(15),
    marginTop: ms(10),
    marginBottom: ms(10),
  },
  successText: {
    color: '#059669',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
  errorMessage: {
    backgroundColor: '#FEE2E2',
    borderRadius: ms(10),
    padding: ms(15),
    marginTop: ms(10),
    marginBottom: ms(10),
  },
  validateButtonDisabled: {
    backgroundColor: '#A9A9A9',
    opacity: 0.7,
  },
  maxAttemptsContainer: {
    marginTop: ms(20),
  },
  maxAttemptsText: {
    color: '#EF4444',
    fontSize: ms(14, 0.3),
    textAlign: 'center',
    marginBottom: ms(15),
  },
  requestNewButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(15),
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#29BFC0',
    marginBottom: ms(10),
  },
  requestNewButtonText: {
    color: '#29BFC0',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  skipButton: {
    backgroundColor: '#F3F6F8',
    borderRadius: ms(15),
    padding: ms(15),
    alignItems: 'center',
  },
  skipButtonText: {
    color: '#6B7280',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  noInvitationContainer: {
    backgroundColor: '#FEF3C7',
    borderRadius: ms(15),
    padding: ms(20),
    marginTop: ms(20),
  },
  noInvitationText: {
    color: '#92400E',
    fontSize: ms(14, 0.3),
    textAlign: 'center',
    marginBottom: ms(15),
  },
});

export default UserOnboardingScreen; 